<template>
  <div class="wholesaler-home">
    <!-- 顶部导航 -->
    <header class="header">
      <div class="header-left">
        <span class="header-title">📦 NAPORLUN</span>
      </div>
      <button class="header-icon" @click="$router.push('/my-profile')">☰</button>
    </header>

    <!-- 首页内容 -->
    <div class="home-content">
      <!-- 横幅 -->
      <div class="home-banner">
        <h2>{{ $t('wholesaler.Home.welcomeBack') }}，{{ userName }}</h2>
        <p>{{ $t('wholesaler.Home.line16_text') }}</p>
      </div>

      <!-- 统计数据 -->
      <div class="stats-tabs">
        <button
          :class="['stats-tab', { active: statsPeriod === 'today' }]"
          @click="statsPeriod = 'today'"
        >{{ $t('wholesaler.Home.line24_text') }}</button>
        <button
          :class="['stats-tab', { active: statsPeriod === 'yesterday' }]"
          @click="statsPeriod = 'yesterday'"
        >{{ $t('wholesaler.Home.line28_text') }}</button>
      </div>
      <div class="stats-row">
        <div class="stat-card">
          <span class="stat-num">{{ currentStats.orderCount || 0 }}</span>
          <span class="stat-label">{{ $t('wholesaler.Home.line33_text') }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-num">S$ {{ formatAmount(currentStats.totalAmount) }}</span>
          <span class="stat-label">{{ $t('wholesaler.Home.line37_text') }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-num success">S$ {{ formatAmount(currentStats.paidAmount) }}</span>
          <span class="stat-label">{{ $t('wholesaler.Home.line41_text') }} <span v-if="currentStats.paidForOld && currentStats.paidForOld !== '0.00'" style="font-size:10px;color:#999;">(含非今日S$ {{ formatAmount(currentStats.paidForOld) }})</span></span>
        </div>
        <div class="stat-card">
          <span class="stat-num warning">S$ {{ formatAmount(currentStats.unpaidAmount) }}</span>
          <span class="stat-label">{{ $t('wholesaler.Home.line45_text') }}</span>
        </div>
        <div class="stat-card">
          <span class="stat-num info">S$ {{ formatAmount(currentStats.refundAmount) }}</span>
          <span class="stat-label">{{ $t('wholesaler.Home.line49_text') }}</span>
        </div>
      </div>

      <!-- 📌 销售 -->
      <div class="section-title">
        <h3>📌 {{ $t('wholesaler.Home.categorySales') }}</h3>
      </div>
      <div class="quick-actions">
        <div class="action-btn" @click="$router.push('/wholesaler/order-on-behalf')">
          <div class="action-icon blue">📝</div>
          <span>{{ $t('wholesaler.Home.line60_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/orders')">
          <div class="action-icon blue">📋</div>
          <span>{{ $t('wholesaler.MyProfile.orderManagement') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/after-sales')">
          <div class="action-icon red">🔄</div>
          <span>{{ $t('wholesaler.Home.line88_text') }}</span>
        </div>
      </div>

      <!-- 📦 商品 & 进货 -->
      <div class="section-title">
        <h3>📦 {{ $t('wholesaler.Home.categoryProductPurchase') }}</h3>
      </div>
      <div class="quick-actions">
        <div class="action-btn" @click="goToProductManagement">
          <div class="action-icon green">🏪</div>
          <span>{{ $t('wholesaler.Home.line64_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/sub-categories')">
          <div class="action-icon blue">📂</div>
          <span>{{ $t('wholesaler.Home.line92_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/wholesale-price-list')">
          <div class="action-icon red">💰</div>
          <span>{{ $t('wholesaler.Home.wholesalePrice') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/supplier-list')">
          <div class="action-icon orange">🏭</div>
          <span>{{ $t('wholesaler.Home.line84_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/purchase-list')">
          <div class="action-icon orange">📦</div>
          <span>{{ $t('wholesaler.Home.line72_text') }}</span>
        </div>
      </div>

      <!-- 🚚 配送 -->
      <div class="section-title">
        <h3>🚚 {{ $t('wholesaler.Home.categoryDelivery') }}</h3>
      </div>
      <div class="quick-actions">
        <div class="action-btn" @click="$router.push('/wholesaler/delivery-tasks')">
          <div class="action-icon purple">🚚</div>
          <span>{{ $t('wholesaler.Home.line68_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/driver-management')">
          <div class="action-icon purple">🧑‍✈️</div>
          <span>{{ $t('wholesaler.MyProfile.driverManagement') }}</span>
        </div>
      </div>

      <!-- 📊 报表 -->
      <div class="section-title">
        <h3>📊 {{ $t('wholesaler.Home.categoryReport') }}</h3>
      </div>
      <div class="quick-actions">
        <div class="action-btn" @click="$router.push('/wholesaler/business-report')">
          <div class="action-icon blue">📊</div>
          <span>{{ $t('wholesaler.Home.line76_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/receivable-report')">
          <div class="action-icon red">💵</div>
          <span>{{ $t('wholesaler.Home.line80_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/income-tax-report')">
          <div class="action-icon blue">📋</div>
          <span>{{ $t('wholesaler.Home.incomeTaxReport') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/gst-report')">
          <div class="action-icon green">🧾</div>
          <span>{{ $t('wholesaler.Home.gstReport') }}</span>
        </div>
      </div>

      <!-- 🏪 店铺 -->
      <div class="section-title">
        <h3>🏪 {{ $t('wholesaler.Home.categoryShop') }}</h3>
      </div>
      <div class="quick-actions">
        <div class="action-btn" @click="shareShop">
          <div class="action-icon pink">🔗</div>
          <span>{{ $t('wholesaler.Home.line96_text') }}</span>
        </div>
        <div class="action-btn" @click="$router.push('/wholesaler/stalls')">
          <div class="action-icon orange">🏬</div>
          <span>{{ $t('wholesaler.MyProfile.stallManagement') }}</span>
        </div>
        <div class="action-btn" @click="$router.push({ name: 'Warehouse' })">
          <div class="action-icon blue">🏬</div>
          <span>{{ $t('wholesaler.MyProfile.warehouseManagement') }}</span>
        </div>
        <div class="action-btn" @click="$router.push({ name: 'PrinterConfig' })">
          <div class="action-icon green">🖨️</div>
          <span>{{ $t('wholesaler.MyProfile.printerSettings') }}</span>
        </div>
      </div>

      <!-- 订单预览 -->
      <div class="section-title">
        <h3>{{ $t('wholesaler.Home.line106_text') }}</h3>
        <a @click="viewAllOrders">{{ $t('wholesaler.Home.line107_text') }}</a>
      </div>
      <div class="order-preview">
        <div
            v-for="order in recentOrders"
            :key="order.id"
            class="order-card"
            @click="goToOrderDetail(order)"
        >
          <div class="order-top">
            <span class="order-id">{{ order.orderNo }}</span>
            <span :class="['order-status', order.status]">{{ order.statusText }}</span>
          </div>
          <div class="order-mid">
            <div class="order-img">📦</div>
            <div class="order-info">
              <h4>{{ order.productName || '商品' }}</h4>
              <p>{{ order.quantity }} × S$ {{ formatAmount(order.price) }}</p>
            </div>
          </div>
          <div class="order-bottom">
            <span class="order-price">S$ {{ formatAmount(order.total) }}</span>
            <span class="order-buyer">{{ order.sellerName }}</span>
          </div>
        </div>
        <el-empty v-if="!loading && recentOrders.length === 0" description="暂无订单" />
      </div>

      <!-- 推荐档口 -->
      <div class="section-title">
        <h3>{{ $t('wholesaler.Home.line137_text') }}</h3>
        <a @click="viewAllStalls">{{ $t('wholesaler.Home.line138_text') }}</a>
      </div>
      <div class="stall-preview">
        <div
            v-for="stall in recommendedStalls"
            :key="stall.id"
            class="stall-card"
            @click="goToStall(stall)"
        >
          <div class="stall-avatar">{{ stall.avatar }}</div>
          <h4>{{ stall.name }}</h4>
          <p class="stall-desc" v-if="stall.description && !isShareCode(stall.description)">{{ stall.description }}</p>
          <div class="stall-tags">
            <span v-if="stall.isVip" class="tag vip">VIP</span>
            <span v-if="stall.isCertified" class="tag certified">{{ $t('wholesaler.Home.line152_text') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')" :class="{ active: currentTab === 'home' }">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>

      <button class="nav-item" @click="navigateTo('products')" :class="{ active: currentTab === 'products' }">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>

      <button class="nav-item" @click="navigateTo('wholesale')" :class="{ active: currentTab === 'wholesale' }">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.Home.line172_text') }}</span>
      </button>

      <button class="nav-item" @click="navigateTo('orders')" :class="{ active: currentTab === 'orders' }">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
        <span v-if="pendingOrders > 0" class="cart-badge">{{ pendingOrders }}</span>
      </button>

      <button class="nav-item" @click="navigateTo('profile')" :class="{ active: currentTab === 'profile' }">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>

    <!-- Toast 提示 -->
    <div v-if="showToast" class="toast-message" :class="toastType">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'WholesalerHome',
  data() {
    return {
      currentTab: 'home',
      showMenu: false,
      statsPeriod: 'today',
      todayStats: {},
      yesterdayStats: {},
      pendingOrders: 0,
      cartCount: 0,
      quickActions: [
        { id: 'wholesale-price', name: this.$t('wholesaler.Home.wholesalePrice'), icon: '💰', color: 'red' }
      ],
      recentOrders: [],
      recommendedStalls: [],
      loading: false,
      showToast: false,
      toastMessage: '',
      toastType: 'success'
    };
  },
  computed: {
    ...mapGetters('auth', ['userName']),
    currentStats() {
      return this.statsPeriod === 'today' ? this.todayStats : this.yesterdayStats;
    }
  },
  mounted() {
    this.fetchHomeStats();
    this.fetchStalls();
  },
  methods: {
    /**
     * 获取首页统计数据
     */
    async fetchHomeStats() {
      this.loading = true;
      try {
        const res = await this.$api.get('/wholesaler/home/stats');
        const data = res.data || res;
        this.todayStats = data.today || {};
        this.yesterdayStats = data.yesterday || {};
        const orders = data.recentOrders || [];
        this.recentOrders = orders.map(o => ({
          id: o.id,
          orderNo: o.orderNo,
          status: o.status,
          statusText: o.statusText,
          productName: o.productName || '商品',
          quantity: o.quantity || 0,
          price: o.price || 0,
          total: o.total || 0,
          sellerName: o.sellerName || '档口',
          createdAt: o.createdAt
        }));
        // 计算待收货数
        this.pendingOrders = 0;
      } catch (error) {
        console.error('加载首页统计失败:', error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * 获取推荐档口
     */
    async fetchStalls() {
      try {
        const res = await this.$api.get('/wholesaler/home/stalls');
        const data = res.data || res;
        this.recommendedStalls = data || [];
      } catch (error) {
        console.error('加载档口列表失败:', error);
      }
    },

    /**
     * ⭐ 跳转到商品管理页面
     */
    goToProductManagement() {
      console.log('跳转到商品管理页面');
      this.$router.push('/wholesaler/product');
    },
    
    /**
     * ⭐ 分享店铺
     */
    async shareShop() {
      let wholesalerId = localStorage.getItem('stored_wholesaler_id') || localStorage.getItem('wholesaler_id');
      
      // 降级：从后端获取当前用户的批发商信息
      if (!wholesalerId) {
        try {
          const res = await this.$api.get('/wholesaler/profile');
          wholesalerId = res?.id || res?.wholesalerId;
          if (wholesalerId) {
            localStorage.setItem('stored_wholesaler_id', wholesalerId.toString());
          }
        } catch (e) {
          console.warn('获取批发商信息失败:', e);
        }
      }
      
      if (!wholesalerId) {
        this.showToastMsg('未找到商户信息');
        return;
      }
      
      // 生成分享链接（使用正式域名）
      const baseUrl = 'https://www.naporlun.com';
      const shareUrl = `${baseUrl}/wholesale/${wholesalerId}`;
      
      // 复制到剪贴板
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(shareUrl).then(() => {
          this.showToastMsg(this.$t('wholesaler.Home.linkCopied'));
        }).catch(() => {
          // 降级方案
          this.fallbackCopy(shareUrl);
        });
      } else {
        this.fallbackCopy(shareUrl);
      }
    },
    
    /**
     * ⭐ 降级复制方案
     */
    fallbackCopy(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this.showToastMsg(this.$t('wholesaler.Home.linkCopied'));
      } catch (err) {
        this.showToastMsg(this.$t('wholesaler.Home.copyFailed') + text);
      }
      document.body.removeChild(textarea);
    },
    
    /**
     * ⭐ 显示 Toast 提示
     */
    showToastMsg(message, type = 'success') {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 2500);
    },
    
    handleAction(action) {
      console.log('handle action', action);
      // 可以根据 action.id 执行不同的操作
      switch (action.id) {
        case 'wholesale-price':
          this.$router.push('/wholesaler/wholesale-price-list');
          break;

        case 'order':
          this.$router.push('/wholesaler/products');
          break;
        case 'refund':
          this.$router.push('/wholesaler/after-sales');
          break;
        case 'delivery-tasks':
          this.$router.push('/wholesaler/delivery-tasks');
          break;
      }
    },
    viewAllOrders() {
      this.$router.push('/wholesaler/orders');
    },
    goToOrderDetail(order) {
      this.$router.push(`/wholesaler/orders/${order.id}`);
    },
    viewAllStalls() {
      this.$router.push('/wholesaler/stalls');
    },
    /**
     * 判断是否为分享码（长随机字符串）
     */
    isShareCode(text) {
      if (!text) return false;
      // 分享码通常是长随机字符串，包含字母数字和特殊字符，长度>20
      return text.length > 20 && /[A-Za-z0-9_\-]{20,}/.test(text);
    },
    goToStall(stall) {
      console.log('go to stall', stall);
    },
    /**
     * 格式化金额：如果小数部分末尾有4个0，则去掉2个
     * 例：0.0000 → 0.00，1.230000 → 1.2300，12.50 → 12.50
     */
    formatAmount(value) {
      if (value === null || value === undefined) return '0.00';
      let str = String(value);
      if (str.endsWith('0000')) {
        str = str.slice(0, -2);
      }
      return str;
    },
    navigateTo(tab) {
      this.currentTab = tab;
      switch (tab) {
        case 'home':
          this.$router.push('/wholesaler/home');
          break;
        case 'products':
          // 跳转到商品列表页面
          this.$router.push('/wholesaler/product');
          break;
        case 'wholesale':
          // 跳转到批发价管理页面
          this.$router.push('/wholesaler/wholesale-price-list');
          break;
        case 'orders':
          this.$router.push('/wholesaler/orders');
          break;
        case 'profile':
          this.$router.push('/my-profile');
          break;
      }
    }
  }
};
</script>

<style scoped>
.wholesaler-home {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 60px;
}

.header {
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}

.header-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #333;
  padding: 0;
}

/* ========== Home Content ========== */
.home-content {
  padding: 16px;
}

.home-banner {
  background: linear-gradient(135deg, #FF6B35 0%, #FF8C61 100%);
  color: #fff;
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 16px;
}

.home-banner h2 {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
}

.home-banner p {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

/* ========== Stats Tabs ========== */
.stats-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.stats-tab {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: #fff;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.stats-tab.active {
  background: #FF6B35;
  color: #fff;
}

/* ========== Stats Row ========== */
.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stat-num {
  display: block;
  font-size: 22px;
  font-weight: 700;
  color: #FF6B35;
  margin-bottom: 4px;
}

.stat-num.success {
  color: #4caf50;
}

.stat-num.warning {
  color: #ff9800;
}

.stat-num.info {
  color: #2196f3;
}

.stat-label {
  font-size: 11px;
  color: #666;
}

/* ========== Section Title ========== */
.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0 12px;
}

.section-title h3 {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.section-title a {
  font-size: 12px;
  color: #FF6B35;
  text-decoration: none;
}

/* ========== Quick Actions ========== */
.quick-actions {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: #fff;
  padding: 12px 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.action-btn:active {
  transform: scale(0.95);
}

.action-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.action-icon.blue { background: #E3F2FD; }
.action-icon.green { background: #E8F5E9; }
.action-icon.orange { background: #FFF3E0; }
.action-icon.purple { background: #F3E5F5; }
.action-icon.red { background: #FFEBEE; }
.action-icon.pink { background: #FCE4EC; }

.action-btn span {
  font-size: 12px;
  color: #333;
}

/* ========== Order Preview ========== */
.order-preview {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.order-card {
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.order-card:last-child {
  border-bottom: none;
}

.order-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.order-id {
  font-size: 12px;
  color: #999;
}

.order-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
}

.order-status.pending {
  background: #FFF3E0;
  color: #FF6B35;
}

.order-status.shipped {
  background: #E3F2FD;
  color: #1976D2;
}

.order-status.done {
  background: #E8F5E9;
  color: #388E3C;
}

.order-mid {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.order-img {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
}

.order-info {
  flex: 1;
}

.order-info h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #333;
}

.order-info p {
  margin: 0;
  font-size: 12px;
  color: #666;
}

.order-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-price {
  font-size: 16px;
  font-weight: 700;
  color: #FF6B35;
}

.order-buyer {
  font-size: 12px;
  color: #999;
}

/* ========== Stall Preview ========== */
.stall-preview {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.stall-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
}

.stall-card:last-child {
  border-bottom: none;
}

.stall-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.stall-card h4 {
  margin: 0 0 4px;
  font-size: 14px;
  color: #333;
}

.stall-desc {
  margin: 0 0 8px;
  font-size: 12px;
  color: #666;
}

.stall-tags {
  display: flex;
  gap: 8px;
}

.tag {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
}

.tag.vip {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #fff;
}

.tag.certified {
  background: #E8F5E9;
  color: #388E3C;
}

/* ========== Bottom Navigation ========== */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e8e8e8;
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-item-icon {
  font-size: 20px;
  line-height: 1;
}

.nav-item span:last-child {
  font-size: 10px;
  color: #999;
}

.nav-item.active span:last-child {
  color: #FF6B35;
}

.nav-item:active {
  transform: scale(0.95);
}

.cart-badge {
  position: absolute;
  top: 5px;
  right: 50%;
  transform: translateX(50%);
  background: #FF6B35;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
}

/* ========== Toast Message ========== */
.toast-message {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 9999;
  animation: fadeIn 0.3s;
}

.toast-message.success {
  background: rgba(76, 175, 80, 0.9);
}

.toast-message.error {
  background: rgba(244, 67, 54, 0.9);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .quick-actions {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
